import React, { useContext, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Card from '../components/common/Card';
import GradientButton from '../components/common/GradientButton';
import Hyperlink from '../components/common/Hyperlink';
import Label from '../components/common/Label';
import FormInput from '../components/FormInput';
import { AuthContext } from '../context/AuthContext';
import GradientBar from './../components/common/GradientBar';
import FormError from './../components/FormError';
import FormSuccess from './../components/FormSuccess';
import { FetchContext } from '../context/FetchContext';
import logo from './../images/logo.png';
import { Redirect } from 'react-router-dom';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import TextError from '../components/TextError.js';

import 'react-phone-number-input/style.css';
import 'react-toastify/dist/ReactToastify.min.css';

const SignupSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  password: Yup.string()
  .required("Please Enter your password")
  .min(5, "Your password must be longer than 7 characters.")
  .max(25)
  .matches(/^(?=.{8,})/, "Must Contain 8 Characters")
  .matches(
    /^(?=.*[a-z])(?=.*[A-Z])/,
    "Must Contain One Uppercase, One Lowercase"
  )
  .matches(
    /^(?=.*[!@#\$%\^&\*])/,
    "Must Contain One Special Case Character"
  )
  .matches(/^(?=.{6,20}$)\D*\d/, "Must Contain One Number"),
});

const Signup = () => {
  const authContext = useContext(AuthContext);
  const fetchContext = useContext(FetchContext);
  const [signupSuccess, setSignupSuccess] = useState();
  const [signupError, setSignupError] = useState();
  const [redirectOnLogin, setRedirectOnLogin] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [mobileNo, setMobileNo] = useState();
  const [errorMessage, setErrorMessage] = useState();

  const submitCredentials = async credentials => {
    try {
      setLoginLoading(true);
      credentials.mobile = mobileNo;
      const { data } = await fetchContext.publicAxios.post(
        `signup`,
        credentials
      );

      authContext.setAuthState(data);
      setSignupSuccess(data.message);
      setSignupError('');

      setTimeout(() => {
        setRedirectOnLogin(true);
      }, 700);
      setErrorMessage(null);
    } catch (error) {
      setLoginLoading(false);
      const { data } = error.response;
      setSignupError(data.message);
      setSignupSuccess('');
    }
  };

  return (
    <>
      {redirectOnLogin && <Redirect to="/login" />}
      {errorMessage && <FormError text={errorMessage} />}
      <section className="w-1/2 h-screen m-auto p-8 sm:pt-10">
        <GradientBar />
        <Card>
          <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full">
              <div>
                <div className="w-32 m-auto mb-6">
                  <img src={logo} alt="Logo" />
                </div>
                <h2 className="mb-2 text-center text-3xl leading-9 font-extrabold text-gray-900">
                  Sign up for an account
                </h2>
                <p className="text-gray-600 text-center">
                  Already have an account?{' '}
                  <Hyperlink to="login" text="Log in now" />
                </p>
              </div>
              <Formik
                initialValues={{
                  firstName: '',
                  lastName: '',
                  email: '',
                  mobile: '',
                  password: '',
                  code: ''
                }}
                onSubmit={values => submitCredentials(values)}
                validationSchema={SignupSchema}
              >
                {() => (
                  <Form className="mt-8">
                    {signupSuccess && <FormSuccess text={signupSuccess} />}
                    {signupError && <FormError text={signupError} />}
                    <input type="hidden" name="remember" value="true" />
                    <div>
                      <div className="flex">
                        <div className="mb-2 mr-2 w-1/2">
                          <div className="mb-1">
                            <Label text="First Name" />
                          </div>
                          <FormInput
                            ariaLabel="First Name"
                            name="firstName"
                            type="text"
                            placeholder="First Name"
                          />
                        </div>
                        <div className="mb-2 ml-2 w-1/2">
                          <div className="mb-1">
                            <Label text="Last Name" />
                          </div>
                          <FormInput
                            ariaLabel="Last Name"
                            name="lastName"
                            type="text"
                            placeholder="Last Name"
                          />
                        </div>
                      </div>
                      <div className="mb-2">
                        <div className="mb-1">
                          <Label text="Email address" />
                        </div>
                        <FormInput
                          ariaLabel="Email address"
                          name="email"
                          type="email"
                          placeholder="Email address"
                        />
                      </div>
                      <div className="mb-2">
                        <div className="mb-1">
                          <Label text="Mobile #" />
                        </div>
                        <PhoneInput
                          placeholder="Mobile #"
                          className="border border-gray-300 rounded p-1 w-full"
                          name="mobile"
                          value={mobileNo}
                          onChange={setMobileNo}
                          error={
                            mobileNo
                              ? isValidPhoneNumber(mobileNo)
                                ? undefined
                                : 'Invalid phone number'
                              : 'Phone number required'
                          }
                        />
                        <ErrorMessage name="mobile" component={TextError} />
                      </div>
                      <div>
                        <div className="mb-1">
                          <Label text="Password" />
                        </div>
                        <FormInput
                          ariaLabel="Password"
                          name="password"
                          type="password"
                          placeholder="Password"
                        />
                      </div>
                      <div className="mb-2">
                        <div className="mb-1">
                          <Label text="Code" />
                        </div>
                        <FormInput
                          ariaLabel="Code"
                          name="code"
                          type="text"
                          placeholder="Code"
                        />
                      </div>
                    </div>

                    <div className="mt-6">
                      <GradientButton
                        type="submit"
                        text="Sign Up"
                        loading={loginLoading}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </Card>
      </section>
    </>
  );
};

export default Signup;
